/**
 * シリーズ詳細の「配信ごとの比較」グラフ（設計書 9.2 / html 図5）。
 *
 * 設計書: `docs/design-docs/deliveries/open-click-analytics.md`。
 * 本文中の `設計書 N.M` はこの md、`html N.M` / `html 図N` は同ディレクトリの
 * `open-click-analytics.html` を指す。
 */
import { LegendItem } from "components/LegendItem";
import { StickyAxisScrollFrame } from "components/StickyAxisScrollFrame";
import {
  AXIS_WIDTH,
  CHART_AXIS_LABEL,
  CHART_AXIS_LABEL_FONT_SIZE,
  CHART_AXIS_LINE,
  CHART_AXIS_TICK_RATIOS,
  CHART_GRID_LINE,
  CHART_GRID_RATIOS,
  CHART_LABEL,
  CHART_TICK_LABEL_BASELINE_OFFSET,
  CHART_TOOLTIP_BACKGROUND,
  CHART_TOOLTIP_TEXT,
  CHART_TOOLTIP_TEXT_DIM,
  NO_DATA_COLOR,
  REACTION_SCALE,
} from "constants/delivery-analytics";
import type { SeriesDeliveryBreakdown } from "hooks/use-delivery-series-api";
import { type FC, useCallback, useLayoutEffect, useRef, useState } from "react";
import {
  axisTickLabel,
  formatCount,
  formatRate,
} from "utils/delivery-analytics";
import { formatDateTime, formatMonthDay } from "utils/relative-time";

/**
 * 棒 1 本の幅・配信ごとの間隔（html 図5 の figcaption）。
 * 間隔は横軸ラベル（`08/05` = 9px の sans-serif で実測およそ 28）が
 * 隣とくっつかない幅を確保するために決めている（1 配信 39 − ラベル 28 = 余白 11）。
 * 配信数によらず 1 配信あたりの幅は一定なので、12 配信でも 30 配信でも
 * ラベルの間隔は変わらず、はみ出す分は横スクロールに逃がす。
 */
export const BAR_WIDTH = 7;
const GROUP_GAP = 18;
export const GROUP_WIDTH = BAR_WIDTH * 3 + GROUP_GAP;
/** 横軸の日付ラベルの文字サイズ */
const DATE_LABEL_FONT_SIZE = 9;
/**
 * 計測対象外の `—` の文字サイズ。em ダッシュは幅 = 1em なので、棒の間隔（7）
 * より大きいと 3 本が地続きの 1 本の線に見える。6 にして間を空ける。
 */
const NO_DATA_FONT_SIZE = 6;
/** 既定で見せる配信数（これを超える分は横スクロール） */
const DEFAULT_VISIBLE = 12;

/**
 * 実画面での拡大率。座標をそのまま px にすると小さすぎるため、座標系（viewBox）は
 * html 図5 のまま維持し、描画サイズだけを一様に拡大する（棒・文字・ツールチップが
 * 同じ比率で大きくなり、レイアウト計算には手を入れない）。
 * 1.5 なのは、既定の 12 配信（y 軸 42 + プロット領域 `plotWidthFor(12)` = 462、
 * 合わせて座標系で 504 = 756px）が、このページのカードの内側に収まる大きさ
 * だから。カードの内側（= スクロール枠）はヘッドレス Chromium の実測で
 * ビューポート 1,440px のとき 1,070px、1,024px のとき 686px
 * （`max-w-6xl` / `max-w-7xl` の頭打ち・サイドバー 192px・
 * ページの `p-6`・カードの `p-4` と枠線を引いた残り）。
 * 1,024px では 756px が収まらないので、そこは横スクロールに逃げる。
 *
 * 左右 2 枚の svg で同じ値を使うこと。片方だけ変えると
 * `preserveAspectRatio` の既定（`xMidYMid meet`）の倍率が食い違い、
 * 目盛と棒の縦位置がずれる。
 */
const RENDER_SCALE = 1.5;

/**
 * プロット領域の左右の余白（座標系）。最初の棒の前と最後の棒の後ろに同じだけ空ける。
 */
export const PLOT_PADDING = 6;

/**
 * プロット領域（右の svg）の座標系の幅。
 *
 * `GROUP_WIDTH` は「棒 3 本 + 次の配信までの間隔」なので、単純に
 * `count * GROUP_WIDTH` にすると最後の配信のうしろにも間隔 18 が残り、
 * 末尾がぽっかり空いて見える（末尾までスクロールしたときに顕著）。
 * 最後の 1 つ分の間隔を引いてから左右の余白を足す。
 * @param {number} count 配信数
 * @return {number} プロット領域の座標系の幅
 */
export const plotWidthFor = (count: number): number =>
  PLOT_PADDING * 2 + count * GROUP_WIDTH - GROUP_GAP;

const CHART_TOP = 14;
const CHART_BOTTOM = 140;
const CHART_HEIGHT = 180;

/** ツールチップの箱と枠のあいだに残す余白（座標系） */
const TOOLTIP_MARGIN = 4;

/** スクロール枠の実測値（px） */
type Viewport = { scrollLeft: number; clientWidth: number };

/** ツールチップの箱を収める範囲（プロット領域の座標系） */
type VisibleRange = { left: number; right: number };

/**
 * いま画面に見えているプロット領域の範囲（プロット領域の座標系）。
 *
 * 箱を `0..plotWidth` にクランプするだけでは足りない。スクロールしていると
 * 画面の左端に来るのは座標系の 0 ではないし、左端の `AXIS_WIDTH` ぶんは
 * sticky な y 軸が上に乗って隠す。そこでスクロール枠の実測値から
 * 「見えている範囲」を出し、そちらでクランプする。
 *
 * 導出（`S` = `RENDER_SCALE`、コンテンツ内 px → プロット座標）:
 * - 行は `[y 軸 42×S px][プロット plotWidth×S px]` の順に並ぶので、
 *   プロット領域の原点はコンテンツ内 px の `AXIS_WIDTH × S`。
 * - 画面に映るコンテンツ内 px は `[scrollLeft, scrollLeft + clientWidth]`。
 * - ただし y 軸は `sticky left-0` なので常に画面左端の `AXIS_WIDTH × S` px を
 *   覆う。実際に見えるのは `[scrollLeft + AXIS_WIDTH × S, scrollLeft + clientWidth]`。
 * - プロット座標に直すと
 *   `left  = (scrollLeft + AXIS_WIDTH × S − AXIS_WIDTH × S) / S = scrollLeft / S`
 *   `right = (scrollLeft + clientWidth − AXIS_WIDTH × S) / S`
 *          `= (scrollLeft + clientWidth) / S − AXIS_WIDTH`
 *
 * 枠が内容より広い（スクロールが出ていない）ときは `right` が `plotWidth` を
 * 超えるので頭を押さえる。`viewport` が未計測（初回描画・jsdom）のときは
 * 座標系いっぱいを返し、従来どおりの挙動に落とす。
 * @param {Viewport | null} viewport スクロール枠の実測値。未計測なら null
 * @param {number} plotWidth プロット領域の座標系の幅
 * @return {VisibleRange} 見えている範囲（left / right）
 */
export const visibleRangeFor = (
  viewport: Viewport | null,
  plotWidth: number,
): VisibleRange => {
  if (viewport === null) return { left: 0, right: plotWidth };
  const left = Math.min(
    Math.max(0, viewport.scrollLeft / RENDER_SCALE),
    plotWidth,
  );
  const right = Math.max(
    left,
    Math.min(
      (viewport.scrollLeft + viewport.clientWidth) / RENDER_SCALE - AXIS_WIDTH,
      plotWidth,
    ),
  );
  return { left, right };
};

/**
 * プロット領域（右の svg）の座標系の幅。
 *
 * 内容ぶんの幅（`plotWidthFor(count)`）と「枠を埋める幅」の大きいほうを取る。
 * - 内容のほうが広ければ、はみ出した分は横スクロールに逃げる。
 * - 枠のほうが広ければ、枠いっぱいまで広げる。ここを固定値（既定の 12 配信ぶん）で
 *   止めると、配信が少ない画面で罫線と x 軸線が途中で切れ、その右に空白が残る。
 *
 * 「枠を埋める幅」は実測から出す。行は `[y 軸 42×S px][プロット W×S px]` なので、
 * `42×S + W×S = clientWidth` を W について解いて `clientWidth / S − AXIS_WIDTH`。
 * これでスクロール枠の内容幅がちょうど枠幅と一致し、右端に空白が出ない。
 *
 * 配信数で枠の大きさは変わらない（枠はカードの幅で決まる）ので、
 * 「10 本でも 30 本でも棒の太さ・間隔・グラフの大きさが同じ」という仕様は
 * 1 配信あたりの幅（`GROUP_WIDTH`）が一定であることだけで満たされる。
 * 配信が少ないときに右側が罫線だけになるのは、その仕様とのトレードオフ。
 *
 * 未計測（初回描画・jsdom・幅 0）のときは既定の 12 配信ぶんに落とす。
 * @param {Viewport | null} viewport スクロール枠の実測値。未計測なら null
 * @param {number} count 配信数
 * @return {number} プロット領域の座標系の幅
 */
export const plotWidthWith = (
  viewport: Viewport | null,
  count: number,
): number => {
  const floor =
    viewport === null
      ? plotWidthFor(DEFAULT_VISIBLE)
      : Math.max(0, viewport.clientWidth / RENDER_SCALE - AXIS_WIDTH);
  return Math.max(plotWidthFor(count), floor);
};

/**
 * スクロール枠を実測する。幅 0（jsdom・`display: none`・マウント直後）は
 * 計測失敗として捨てる。0 を採ると「枠を埋める幅」が負になり、
 * 未計測より悪い状態（`plotWidth` の下限が消える）に落ちるため。
 * @param {HTMLElement} element スクロール枠
 * @return {Viewport | null} 実測値。測れなければ null
 */
const measureViewport = (element: HTMLElement): Viewport | null =>
  element.clientWidth > 0
    ? { scrollLeft: element.scrollLeft, clientWidth: element.clientWidth }
    : null;

type SeriesMetric = "open" | "click" | "reply";

/**
 * 描く 3 指標。棒・凡例・ツールチップの 3 か所がすべてこの 1 本から出る
 * （並行リストにすると順序やラベルが片方だけずれても型が守ってくれない）。
 */
const METRICS: ReadonlyArray<{
  key: SeriesMetric;
  label: string;
  color: string;
  /** 0..1 の比率。計測対象外は null */
  rate: (delivery: SeriesDeliveryBreakdown) => number | null;
  /** ツールチップに併記する実数 */
  count: (delivery: SeriesDeliveryBreakdown) => number | null;
}> = [
  {
    key: "open",
    label: "開封率",
    color: REACTION_SCALE[2].color,
    rate: d => d.openRate,
    count: d => d.uniqueOpens,
  },
  {
    key: "click",
    label: "クリック率",
    color: REACTION_SCALE[5].color,
    rate: d => d.clickRate,
    count: d => d.uniqueClicks,
  },
  {
    key: "reply",
    label: "返信率",
    color: REACTION_SCALE[6].color,
    rate: d => d.replyRate,
    count: d => d.replyRecipients,
  },
];

type HoverState = {
  delivery: SeriesDeliveryBreakdown;
  metric: SeriesMetric;
  /** ホバーした棒の中心の x 座標（プロット領域の座標系） */
  x: number;
  /** ホバーした棒の上端の y 座標（三角で接続する位置） */
  barTop: number;
};

type SeriesComparisonChartProps = {
  /** A2 の配信ごとの内訳（新しい順） */
  deliveries: SeriesDeliveryBreakdown[];
};

/**
 * シリーズ詳細の「配信ごとの比較」グラフ（設計書 9.2 / html 図5）。
 * 横軸＝配信（左が古い順）、縦軸＝率のグループ棒グラフ。積み上げにはしない。
 *
 * 配信が増えると横スクロールが常態になるため、y 軸だけを別の svg に分けて
 * `position: sticky` で左端に貼り付け、プロット領域だけを流す。左右を別々の
 * スクロールコンテナに分けると、横スクロールバーが内容の下に重ならない環境
 * （Windows / Linux）で左右の縦位置がバーの高さぶんずれるため、
 * スクロールボックスは 1 つのままにしている。
 * @param {SeriesComparisonChartProps} props 配信ごとの内訳
 * @return {JSX.Element} グラフ
 */
export const SeriesComparisonChart: FC<SeriesComparisonChartProps> = ({
  deliveries,
}) => {
  const [hover, setHover] = useState<HoverState | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [viewport, setViewport] = useState<Viewport | null>(null);
  /*
    実測値は**常に** ref に溜める。state に載せるのはツールチップが出ている間だけ
    （下の `startHover` / `handleScroll` を参照）。ref に最新値があるので、
    ホバーが始まった時点で測り直す必要がない。
  */
  const viewportRef = useRef<Viewport | null>(null);
  /* スクロール購読から読む「いまホバー中か」。state だと購読の張り替えが要る */
  const isHoveringRef = useRef(false);

  /**
   * 実測値を state に載せる。同じ値なら据え置いて再描画を起こさない。
   * @param {Viewport | null} next 実測値（測れていなければ null）
   * @return {void}
   */
  const commitViewport = useCallback((next: Viewport | null): void => {
    if (next === null) return;
    setViewport(prev =>
      prev !== null &&
      prev.scrollLeft === next.scrollLeft &&
      prev.clientWidth === next.clientWidth
        ? prev
        : next,
    );
  }, []);

  /**
   * スクロール枠を測って ref に溜める（state には触らない）。
   * @return {Viewport | null} 実測値。測れなければ null
   */
  const measure = useCallback((): Viewport | null => {
    const element = scrollRef.current;
    if (element === null) return null;
    const next = measureViewport(element);
    if (next !== null) viewportRef.current = next;
    return next;
  }, []);

  /**
   * スクロール枠を測り直して state にも反映する。
   * @return {void}
   */
  const syncViewport = useCallback((): void => {
    commitViewport(measure());
  }, [commitViewport, measure]);

  /*
    枠の幅（`clientWidth`）は**常時**追随させる。`plotWidth` の下限がここから
    出るので、測れていないと配信が少ない画面で罫線が枠の途中で切れる。
    ウィンドウ幅だけでなくサイドバーの開閉でも枠は変わるので、`resize` イベント
    ではなく `ResizeObserver` で枠そのものを見る。発火はレイアウトが動いたときだけ
    なので、常時購読でも再描画は増えない。

    スクロールも**常時**購読する。購読を `hover` に応じて張り替えると、棒を
    出入りするたびに layout effect が teardown / setup し、setup の中の再計測が
    `clientWidth` を読む —— `useLayoutEffect` は DOM 変更直後・ペイント前に走るので
    これは強制同期レイアウトになり、棒 90 本を撫でると 90 回起きていた。
    購読は張りっぱなしにし、`scrollLeft` は ref に溜めるだけにして、
    `setViewport`（= 再描画）はツールチップが出ている間だけ呼ぶ。
    こうすると横スクロールしても、ツールチップが出ていない間は再描画が起きない。

    `useLayoutEffect` なのはペイント前に初回計測を間に合わせるため。
  */
  useLayoutEffect(() => {
    const element = scrollRef.current;
    if (element === null) return;
    syncViewport();

    /**
     * 横スクロールのたびに ref を更新し、ホバー中だけ再描画する。
     * @return {void}
     */
    const handleScroll = (): void => {
      const next = measure();
      if (isHoveringRef.current) commitViewport(next);
    };
    element.addEventListener("scroll", handleScroll, { passive: true });

    // jsdom には ResizeObserver が無い。初回計測だけで済ませる
    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(syncViewport);
    observer?.observe(element);

    return () => {
      element.removeEventListener("scroll", handleScroll);
      observer?.disconnect();
    };
  }, [syncViewport, measure, commitViewport]);

  /**
   * ホバー開始。ref に溜めておいた最新の実測値を state に載せる
   * （ここで測り直さないのが肝。測ると強制同期レイアウトになる）。
   * @param {HoverState} next ホバーした棒
   * @return {void}
   */
  const startHover = useCallback(
    (next: HoverState): void => {
      isHoveringRef.current = true;
      commitViewport(viewportRef.current);
      setHover(next);
    },
    [commitViewport],
  );

  /**
   * ホバー終了。
   * @return {void}
   */
  const endHover = useCallback((): void => {
    isHoveringRef.current = false;
    setHover(null);
  }, []);

  // 左が古い順
  const items = [...deliveries].reverse();
  if (items.length === 0) {
    return (
      <p className="text-sm text-base-content/60">
        比較できる配信がまだありません
      </p>
    );
  }

  const maxRate = Math.max(
    0.1,
    ...items.flatMap(d => [
      d.openRate ?? 0,
      d.clickRate ?? 0,
      d.replyRate ?? 0,
    ]),
  );
  // 10% 刻みの切りの良い上限にそろえる
  const axisMax = Math.ceil(maxRate * 10) / 10;
  // プロット領域（右の svg）の座標系の幅。内容ぶんと「枠を埋める幅」の広いほう
  const plotWidth = plotWidthWith(viewport, items.length);
  // ツールチップの箱を収める範囲。座標系いっぱいではなく「いま見えている範囲」
  const visible = visibleRangeFor(viewport, plotWidth);

  /**
   * 率を y 座標に変換する
   * @param {number} rate 0..1 の比率
   * @return {number} y 座標
   */
  const toY = (rate: number): number =>
    CHART_BOTTOM - ((CHART_BOTTOM - CHART_TOP) * rate) / axisMax;

  return (
    <>
      <div className="mb-2 flex flex-wrap items-center gap-3 text-[11px] text-slate-600">
        {METRICS.map(metric => (
          <LegendItem
            key={metric.key}
            color={metric.color}
            label={metric.label}
          />
        ))}
      </div>
      {/*
        枠はカードの幅いっぱい（幅を固定しない）。`max-width` で押さえると、
        カードの内側との差がそのまま右の空白になる。
        グラフの大きさが配信数で変わらないことは 1 配信あたりの幅が一定
        （`GROUP_WIDTH`）であることで担保されるので、枠を固定する必要は無い。

        スクロール枠・行・sticky な y 軸を成り立たせる 5 クラスは
        `StickyAxisScrollFrame` が持つ（不変条件の所有者を 1 つにするため）。
        ここは y 軸とプロット領域の中身だけを渡す。
      */}
      <StickyAxisScrollFrame
        ref={scrollRef}
        axis={
          <svg
            width={AXIS_WIDTH * RENDER_SCALE}
            height={CHART_HEIGHT * RENDER_SCALE}
            viewBox={`0 0 ${AXIS_WIDTH} ${CHART_HEIGHT}`}
            aria-hidden="true"
          >
            {/*
              縦線は座標系の右端に接する。中心を AXIS_WIDTH に置くと
              右半分が viewBox の外で切れて線が細くなるため 0.5 内側に寄せる。
            */}
            <line
              x1={AXIS_WIDTH - 0.5}
              y1={CHART_TOP}
              x2={AXIS_WIDTH - 0.5}
              y2={CHART_BOTTOM}
              stroke={CHART_AXIS_LINE}
            />
            <g
              fill={CHART_AXIS_LABEL}
              fontFamily="sans-serif"
              fontSize={CHART_AXIS_LABEL_FONT_SIZE}
            >
              {CHART_AXIS_TICK_RATIOS.map(ratio => (
                <text
                  key={ratio}
                  x={6}
                  y={toY(axisMax * ratio) + CHART_TICK_LABEL_BASELINE_OFFSET}
                >
                  {`${axisTickLabel(axisMax * ratio)}%`}
                </text>
              ))}
              <text x={30} y={CHART_BOTTOM + CHART_TICK_LABEL_BASELINE_OFFSET}>
                0
              </text>
              <text x={AXIS_WIDTH - 2} y={10} textAnchor="end">
                率
              </text>
            </g>
          </svg>
        }
        plot={
          /* プロット領域。原点は y 軸の右端（旧座標系の x = AXIS_WIDTH） */
          <svg
            width={plotWidth * RENDER_SCALE}
            height={CHART_HEIGHT * RENDER_SCALE}
            viewBox={`0 0 ${plotWidth} ${CHART_HEIGHT}`}
            role="img"
            aria-label="配信ごとの開封率・クリック率・返信率のグループ棒グラフ。左が古い配信"
          >
            <title>配信ごとの開封率・クリック率・返信率</title>
            <line
              x1={0}
              y1={CHART_BOTTOM}
              x2={plotWidth}
              y2={CHART_BOTTOM}
              stroke={CHART_AXIS_LINE}
            />
            <g stroke={CHART_GRID_LINE}>
              {CHART_GRID_RATIOS.map(ratio => (
                <line
                  key={ratio}
                  x1={0}
                  y1={toY(axisMax * ratio)}
                  x2={plotWidth}
                  y2={toY(axisMax * ratio)}
                />
              ))}
            </g>

            {items.map((delivery, groupIndex) => {
              const groupX = PLOT_PADDING + groupIndex * GROUP_WIDTH;
              return (
                <g key={delivery.deliveryId}>
                  {METRICS.map((metric, metricIndex) => {
                    const rate = metric.rate(delivery);
                    const x = groupX + metricIndex * BAR_WIDTH;
                    if (rate === null) {
                      return (
                        <text
                          key={metric.key}
                          x={x + BAR_WIDTH / 2}
                          y={CHART_BOTTOM - 3}
                          fontSize={NO_DATA_FONT_SIZE}
                          fill={NO_DATA_COLOR}
                          textAnchor="middle"
                        >
                          —
                        </text>
                      );
                    }
                    return (
                      <rect
                        key={metric.key}
                        x={x}
                        y={toY(rate)}
                        width={BAR_WIDTH}
                        height={Math.max(0, CHART_BOTTOM - toY(rate))}
                        fill={metric.color}
                        onMouseEnter={() =>
                          startHover({
                            delivery,
                            metric: metric.key,
                            x: x + BAR_WIDTH / 2,
                            barTop: toY(rate),
                          })
                        }
                        onMouseLeave={endHover}
                      />
                    );
                  })}
                  <text
                    x={groupX + (BAR_WIDTH * 3) / 2}
                    y={CHART_BOTTOM + 14}
                    fontSize={DATE_LABEL_FONT_SIZE}
                    fill={CHART_LABEL}
                    fontFamily="sans-serif"
                    textAnchor="middle"
                  >
                    {formatMonthDay(delivery.sentAt)}
                  </text>
                </g>
              );
            })}

            {hover && (
              <ChartTooltip
                delivery={hover.delivery}
                metric={hover.metric}
                x={hover.x}
                barTop={hover.barTop}
                visibleLeft={visible.left}
                visibleRight={visible.right}
              />
            )}
          </svg>
        }
      />
    </>
  );
};

type ChartTooltipProps = {
  delivery: SeriesDeliveryBreakdown;
  metric: SeriesMetric;
  x: number;
  /** ホバーした棒の上端（三角で接続する位置） */
  barTop: number;
  /** いま画面に見えているプロット領域の左端（この右側に箱を収める） */
  visibleLeft: number;
  /** いま画面に見えているプロット領域の右端（この左側に箱を収める） */
  visibleRight: number;
};

/**
 * ホバー中の配信のツールチップ（ホバーした指標だけ太字・実数を併記）。
 * 箱は「いま画面に見えている範囲」の左右と、プロット領域の上端の内側に
 * クランプする。三角が指す位置（棒の中心）は動かさず、箱だけをずらす。
 * @param {ChartTooltipProps} props 配信・指標・位置・見えている範囲
 * @return {JSX.Element | null} ツールチップ
 */
const ChartTooltip: FC<ChartTooltipProps> = ({
  delivery,
  metric,
  x,
  barTop,
  visibleLeft,
  visibleRight,
}) => {
  const boxWidth = 160;
  const boxHeight = 62;
  /*
    箱を見えている範囲の中に収める。
    - 下限を `visibleLeft` から取るのが肝。`0`（座標系の左端）で止めると、
      スクロール後に画面左端へ来た棒の箱が sticky な y 軸の裏へ潜り込む。
    - 上限も `plotWidth` ではなく `visibleRight`。最右の棒だけでなく、
      スクロール途中で画面右端に来た棒でも枠の外に出ない。
    - `maxX` に下限を噛ませてあるのは、枠が箱より狭いとき（`visibleRight −
      visibleLeft < boxWidth + TOOLTIP_MARGIN × 2` = 168）に上下が逆転して
      左へ飛び出すのを防ぐため。座標系の 168 は
      `clientWidth = (168 + AXIS_WIDTH) × RENDER_SCALE = 315px` に当たる
      （下限を外すと箱がプロット領域の外＝負の x に出るのは `clientWidth ≤ 308px`）。
      その場合は左に寄せて右へはみ出す（y 軸の裏に隠れるよりましなため）。
      この幅には**到達する**。`Layout` のサイドバーが `w-48`（192px）固定で
      畳まれないため、ビューポート 375px では枠が 69px しか残らない（実測）。
      そこまで狭いとページ全体が使い物にならないので追加の手当てはしないが、
      「`max-w-6xl` だから到達しない」ではないので消さないこと。
  */
  const minX = visibleLeft + TOOLTIP_MARGIN;
  const maxX = Math.max(minX, visibleRight - boxWidth - TOOLTIP_MARGIN);
  const boxX = Math.min(Math.max(minX, x - boxWidth / 2), maxX);
  // 率が軸上限に近い棒では上にはみ出してプロット領域の外へ出るため、
  // 罫線の上端より下で止める
  const boxY = Math.max(CHART_TOP + 4, barTop - boxHeight - 6);
  // 三角は棒の中心（x）を指したままにする。箱の角丸から出ないよう、
  // 箱の内側 8 までで頭を押さえるだけで、箱と一緒に動かしてはいけない。
  const tipX = Math.min(Math.max(x, boxX + 8), boxX + boxWidth - 8);

  // 実数はホバーした指標にだけ併記する
  const rows = METRICS.map(row => ({
    key: row.key,
    text: `${row.label} ${formatRate(row.rate(delivery))}${
      row.key === metric ? countSuffix(row.count(delivery)) : ""
    }`,
  }));

  return (
    <g>
      <rect
        x={boxX}
        y={boxY}
        width={boxWidth}
        height={boxHeight}
        rx={4}
        fill={CHART_TOOLTIP_BACKGROUND}
      />
      <path
        d={`M${tipX - 4} ${boxY + boxHeight} L${tipX} ${boxY + boxHeight + 5} L${tipX + 4} ${boxY + boxHeight} Z`}
        fill={CHART_TOOLTIP_BACKGROUND}
      />
      <text
        x={boxX + 8}
        y={boxY + 14}
        fontSize={9}
        fill={CHART_TOOLTIP_TEXT}
        fontFamily="sans-serif"
      >
        {formatDateTime(delivery.sentAt)}
      </text>
      {rows.map((row, index) => (
        <text
          key={row.key}
          x={boxX + 8}
          y={boxY + 28 + index * 12}
          fontSize={9}
          fontFamily="sans-serif"
          fill={
            row.key === metric ? CHART_TOOLTIP_TEXT : CHART_TOOLTIP_TEXT_DIM
          }
          fontWeight={row.key === metric ? 700 : 400}
        >
          {row.text}
        </text>
      ))}
    </g>
  );
};

/**
 * ツールチップに併記する実数。
 * @param {number | null} value 宛先数
 * @return {string} 括弧書きの実数（無ければ空文字）
 */
function countSuffix(value: number | null): string {
  return value === null ? "" : `（${formatCount(value)} 宛先）`;
}
